/**
 * Color manipulation utilities for theme-aware interactive objects
 */
import { isDarkTheme } from "../config/themes";

/**
 * Convert hex color to HSL format
 * @param {string} hex - Hex color string (e.g., "#FF6B35")
 * @returns {object} - HSL object { h, s, l }
 */
function hexToHsl(hex) {
  // Remove # if present
  const cleanHex = hex.replace("#", "");

  // Parse RGB values
  const r = parseInt(cleanHex.substr(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substr(2, 2), 16) / 255;
  const b = parseInt(cleanHex.substr(4, 2), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Convert HSL to hex color
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} - Hex color string
 */
function hslToHex(h, s, l) {
  h /= 360;
  s /= 100;
  l /= 100;

  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);

  const toHex = (c) => {
    const hex = c.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Darken a color by reducing its lightness
 * @param {string} color - Hex color string
 * @param {number} amount - Amount to darken (0-100, default 20)
 * @returns {string} - Darkened hex color
 */
export function darkenColor(color, amount = 20) {
  if (!color || typeof color !== "string") return color;

  const hsl = hexToHsl(color);
  hsl.l = Math.max(0, hsl.l - amount);

  return hslToHex(hsl.h, hsl.s, hsl.l);
}

/**
 * Lighten a color by increasing its lightness
 * @param {string} color - Hex color string
 * @param {number} amount - Amount to lighten (0-100, default 20)
 * @returns {string} - Lightened hex color
 */
export function lightenColor(color, amount = 20) {
  if (!color || typeof color !== "string") return color;

  const hsl = hexToHsl(color);
  hsl.l = Math.min(100, hsl.l + amount);

  return hslToHex(hsl.h, hsl.s, hsl.l);
}

/**
 * Get theme-appropriate fill color based on the drawing color and current theme
 * @param {string} drawingColor - The selected drawing color from color picker
 * @param {string} theme - Current theme (registry name, e.g. 'lara-dark-indigo')
 * @param {number} darkenAmount - Amount to darken for dark theme (default 25)
 * @param {number} lightenAmount - Amount to lighten for light theme (default 30)
 * @returns {string} - Theme-appropriate fill color
 */
export function getThemeAwareFillColor(
  drawingColor,
  theme,
  darkenAmount = 25,
  lightenAmount = 30,
) {
  if (!drawingColor || typeof drawingColor !== "string") return drawingColor;

  if (isDarkTheme(theme)) {
    // Dark theme: use darker shade for fill
    return darkenColor(drawingColor, darkenAmount);
  } else {
    // Light theme: use lighter shade for fill
    return lightenColor(drawingColor, lightenAmount);
  }
}

/**
 * Adjust opacity for theme-appropriate visibility
 * @param {string} theme - Current theme
 * @param {number} baseOpacity - Base opacity value (default 0.15)
 * @returns {number} - Theme-appropriate opacity
 */
export function getThemeAwareFillOpacity(theme, baseOpacity = 0.15) {
  const dark = isDarkTheme(theme);

  // Dark theme needs slightly higher opacity for visibility
  return dark ? Math.min(1, baseOpacity * 1.2) : baseOpacity;
}

/**
 * Get edge circle colors for maximum visibility
 * @param {string} theme - Current theme (registry name, e.g. 'lara-dark-indigo')
 * @returns {object} - { stroke, fill } colors for edge circles
 */
export function getEdgeCircleColors(theme) {
  if (isDarkTheme(theme)) {
    return {
      stroke: "#FFFFFF", // White stroke
      fill: "#000000", // Black fill
    };
  } else {
    return {
      stroke: "#000000", // Black stroke
      fill: "#FFFFFF", // White fill
    };
  }
}
